
const OAUTH_REQUEST_PARAMS_STORAGE_KEY = "oauth_request_params";

export function storeOAuthRequestParams(search: string): void {
  try {
    localStorage.setItem(OAUTH_REQUEST_PARAMS_STORAGE_KEY, search);
  } catch {
    // Ignore storage failures.
  }
}

export function hasOAuthClientIdContext(search: string): boolean {
  const fromUrl = new URLSearchParams(search);
  if (fromUrl.has("client_id")) {
    return true;
  }
  const stored = getOAuthRequestParams();
  return stored.includes("client_id=");
}

export function getOAuthRequestParams(): string {
  try {
    return localStorage.getItem(OAUTH_REQUEST_PARAMS_STORAGE_KEY) ?? "";
  } catch {
    // Ignore storage failures.
    return "";
  }
}

export function clearOAuthRequestParams(): void {
  try {
    localStorage.removeItem(OAUTH_REQUEST_PARAMS_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function normalizeOAuthParamSource(source: string): string {
  return source.startsWith("?") ? source.slice(1) : source;
}

/**
 * Prefer live URL OAuth query when it contains client_id; otherwise use persisted params.
 */
export function resolveOAuthRequestParams(liveSearch: string): string {
  const normalized = normalizeOAuthParamSource(liveSearch);
  if (new URLSearchParams(normalized).has("client_id")) {
    return normalized;
  }
  return getOAuthRequestParams();
}

export function hasRequiredOAuthAuthorizeParams(paramSource: string): boolean {
  const params = new URLSearchParams(normalizeOAuthParamSource(paramSource));
  const requiredKeys = ["client_id", "redirect_uri", "response_type"] as const;
  return requiredKeys.every((key) => {
    const value = params.get(key)?.trim();
    return value != null && value.length > 0;
  });
}

/**
 * Extract redirect_url from URL search params or encoded Okta state.
 */
export function getRedirectUrlFromSearch(search: string): string | null {
  const searchParams = new URLSearchParams(search);
  const redirectUrlFromQuery = searchParams.get("redirect_url")?.trim();

  if (redirectUrlFromQuery) {
    return redirectUrlFromQuery;
  }

  const stateParam = searchParams.get("state");
  if (!stateParam) {
    return null;
  }

  const parsedStateCandidates = [stateParam];

  try {
    parsedStateCandidates.push(decodeURIComponent(stateParam));
  } catch {
    // Ignore malformed percent-encoding.
  }

  for (const stateCandidate of parsedStateCandidates) {
    try {
      const decodedState = JSON.parse(stateCandidate) as { redirect_url?: string };
      const redirectUrl = decodedState.redirect_url?.trim();
      if (redirectUrl) {
        return redirectUrl;
      }
    } catch {
      // Ignore non-JSON state and continue to base64 decode attempts.
    }

    try {
      const decodedState = JSON.parse(atob(stateCandidate)) as { redirect_url?: string };
      const redirectUrl = decodedState.redirect_url?.trim();
      if (redirectUrl) {
        return redirectUrl;
      }
    } catch {
      // Ignore invalid base64 state values.
    }
  }

  return null;
}

/**
 * Get OAuth configuration from runtime config with fallbacks
 * Allows environment-specific configuration at runtime
 */
function getOAuthConfig(): { issuer: string; clientId: string; scope: string } {
  const runtimeConfig = (window as any).__authShellConfig;

  const scopeRaw = runtimeConfig?.getConfigValue?.("oauthAuthorizeScope") as
    | string
    | undefined;
  // Env-specific scopes must come from config only (no customerweb-* default).
  const scope =
    typeof scopeRaw === "string" && scopeRaw.trim().length > 0
      ? scopeRaw.trim()
      : "";

  return {
    issuer:
      runtimeConfig?.getConfigValue?.("oauthIssuer") ||
      "https://identity-dev.freedommortgage.com/oauth2/aus1ibokscAaJ3iGh5d7",
    clientId:
      runtimeConfig?.getConfigValue?.("oauthClientId") ||
      "0oa2h5pfwwv0M23NV5d7",
    scope,
  };
}

export function buildOAuthAuthorizeUrl(
  sessionToken: string,
  liveSearch = "",
): string {
  const oauthConfig = getOAuthConfig();
  const authorizeBaseUrl = `${oauthConfig.issuer}/v1/authorize`;
  const resolved = resolveOAuthRequestParams(liveSearch);
  const currentUrlParams = new URLSearchParams(resolved);
  currentUrlParams.set("sessionToken", sessionToken);

  if (!currentUrlParams.has("scope") && oauthConfig.scope) {
    currentUrlParams.set("scope", oauthConfig.scope);
  }

  clearOAuthRequestParams();
  return `${authorizeBaseUrl}?${currentUrlParams.toString()}`;
}

/**
 * Continue OAuth using an existing Okta browser session (SSO cookie), without sessionToken.
 */
export function buildOAuthAuthorizeUrlForExistingSession(
  liveSearch = "",
): string | null {
  const oauthConfig = getOAuthConfig();
  const resolved = resolveOAuthRequestParams(liveSearch);
  if (!hasRequiredOAuthAuthorizeParams(resolved)) {
    return null;
  }

  const authorizeBaseUrl = `${oauthConfig.issuer}/v1/authorize`;
  const currentUrlParams = new URLSearchParams(resolved);

  if (!currentUrlParams.has("scope") && oauthConfig.scope) {
    currentUrlParams.set("scope", oauthConfig.scope);
  }
  currentUrlParams.delete("sessionToken");

  clearOAuthRequestParams();
  return `${authorizeBaseUrl}?${currentUrlParams.toString()}`;
}
